import { Component, OnInit, input, computed, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { addIcons } from 'ionicons';
import { arrowForward, helpCircleOutline } from 'ionicons/icons';
import {
  IonContent, IonHeader, IonTitle, IonToolbar, IonButtons, IonButton, IonIcon, IonPopover,
  IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCardSubtitle, IonNote
} from '@ionic/angular/standalone';
import { StorageService } from '../storage.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SubtitleDirective } from '../directives/subtitle.directive';
import { IStateUserInfo } from '../user/user.page';
import { LANG_KEY } from 'src/app/app.component';
import { VIEW_KEY } from '../user/user.page';
import { environment } from 'src/environments/environment';
import { ApiService } from '../api.service';
import { Browser } from '@capacitor/browser';
import { forkJoin, switchMap, take, map, tap, filter } from 'rxjs';
import { FirebaseMessagingService } from '../app.firebase-messaging.service';
import { LandBotDirective } from '../directives/land-bot.directive';

interface IEPI {
    company_worker_id: number;
    risk_source_type_id: number;
    risk_source_id: number;
    company_proteccions_id: number;
    company_protections_name: string;
    sent: string | null;
    useful_life: string | null;
    company_proteccions_workers_deliveries_document_id: number | null;
    company_proteccions_workers_id: number;
    company_proteccions_workers_deliveries_id: number | null;
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.page.html',
  styleUrls: ['./dashboard.page.scss'],
  standalone: true,
  imports: [
    IonContent, IonHeader, IonTitle, IonToolbar, CommonModule, FormsModule, IonButtons,
    IonButton, SubtitleDirective, IonIcon, RouterLink, TranslateModule, IonCard,
    IonCardContent, IonCardHeader, IonCardTitle, IonCardSubtitle, IonNote, IonPopover, LandBotDirective
  ]
})
export class DashboardPage implements OnInit {

  detail = input.required<{
    userInfo: IStateUserInfo,
    epis: IEPI[],
    userCompanies: { [k: string]: string },
    languages: { [k: string]: string },
    userLanguage: { language_id: string }, // CAT
    isPRL: boolean,
    company: { id: string, name: string }
  }>();

  private _updatedEpis?: IEPI[];
  unsignedEpis = computed(()=>{
    const { epis } = !!this._updatedEpis?{epis:this._updatedEpis}:this.detail();
    return epis.filter(epi=>!!epi.company_proteccions_workers_deliveries_id && !epi.company_proteccions_workers_deliveries_document_id );
  })

  unsignedDeliveries = computed(()=>{
    const uepis = this.unsignedEpis();
    return Object.entries(uepis.reduce((a, b)=>{
      const delivery_d = b.sent as string;
      if (!a[delivery_d]) a[delivery_d] = [b];
      else a[delivery_d].push(b);
      return a;
    }, {} as {[delivery_date:string]:IEPI[]}));
  });

  view: string = '';

  notis$ = this.pushNotis.liveNotis$.pipe(
    // map(live=>{ return {live, tray:this.pushNotis.deliveredNotifications }; }),
    // filter(spur=>!!spur.live.length),
    tap((evt)=>this.debug(evt, 'notis$'))
  );

  discardNoti(index: number) {
    this.pushNotis.discardLiveNoti(index).catch(
      err=>this.debug(err, 'discarding noti')
    );
  }
  

  constructor(
    private translate: TranslateService,
    private storage: StorageService,
    private api: ApiService,
    private cdr: ChangeDetectorRef,
    private pushNotis: FirebaseMessagingService,
  ) {
    addIcons({
      bell: 'assets/icon/bell.svg',
      profile: 'assets/icon/profile.svg',
      signature: 'assets/icon/signature.svg',
      fwd: arrowForward,
      helpCircleOutline
    })
  }

  ngOnInit(): void {
    this.debug(this.detail(), 'got detail');
  }

  async ionViewWillEnter() {
    try {
      // Carregar la vista desada
      const view = await this.storage.ready.then(() => this.storage.getCache(VIEW_KEY, 0));
      if (view) {
        this.view = view;
        this.debug({ view }, 'view');
      }
      // Carregar les traduccions
      const dtl = this.detail();
      const curlang = this.translate.currentLang;
      const uplang = dtl.userLanguage.language_id;

      this.debug({ curlang, uplang }, 'langs:');

      if (uplang) {
        const rxp = new RegExp(`^${curlang}`, 'i');
        if (!rxp.test(uplang)) { // Discrepància amb `uplang`, cal reset.
          await this.resetLanguage(uplang);
        }
      }
    } catch (error) {
      console.error('Error al inicialitzar el dashboard:', error);
    }
  }

  private async resetLanguage(uplang: string) {
    const tlangs = this.translate.getLangs();
    const nlang = tlangs.find((lc) => uplang.indexOf(lc.toUpperCase()) === 0);

    this.debug({ tlangs, nlang }, 'finding target language');

    if (nlang) {
      await this.storage.ready;
      await this.storage.set(LANG_KEY, nlang);
      this.translate.use(nlang);
      this.debug('language reset success!');
    }
  }

  requestSignature({company_proteccions_workers_deliveries_id}:{company_proteccions_workers_deliveries_id: number|null}) {
    const {company} = this.detail();
    this.api.signIPEDelivery(
      company_proteccions_workers_deliveries_id!, company.id
    ).pipe(
      this.api.uify()
    ).subscribe({
      next:({url})=>{
        const dtl = this.detail();
        this.storage.setCache(`ipeWorkers_${dtl.company.id}_${dtl.userInfo.contact_id}`, null);
        this.storage.setCache(`getIPEDeliveryStatus_${dtl.company.id}_${dtl.userInfo.contact_id}`, null);
        Browser.open({url, windowName: '_self'}).then(()=>{
          // En aquest punt, subscriure's a aquesta senyal (take(1)), i refrescar l'estatus i epis del usuari.
          this.api.signIPEDeliveryCallbackCalled$.pipe(
            take(1),
            switchMap(()=>forkJoin({
              ipes: this.api.ipeWorkers(dtl.company.id, dtl.userInfo.contact_id),
              status: this.api.getIPEDeliveryStatus(dtl.company.id, dtl.userInfo.contact_id)
            })),
          ).subscribe({
            next:({ipes})=>{
              this._updatedEpis = (<any>ipes) as IEPI[];
              this.cdr.detectChanges();
            }
          });
        }).catch(err=>this.debug(err, 'failure'))
      }
    });
  }


  debug(sth: any, tag = "") {
    if (environment.production) return;
    console.log(`DEBUG ${this.constructor.name} ${tag}`, sth);
  }
}
